import { Suspense, lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { LoadingText } from "views/components/loading";
import { Generate2FA } from "views/containers/auth/generate-2fa";
import AuthLayout from "views/layouts/auth";
import DashboardLayout from "views/layouts/dashboard";
import Login from "views/pages/authentication/login";
import Unauthorized from "views/pages/unauthorized/401";
import NotFound from "views/pages/unauthorized/404";
import PrivateRoute from "./privateRoute";

// Lazy load page components
const Dashboard = lazy(() => import("views/pages/dashboard"));
const Request = lazy(() => import("views/containers/request"));
const UserInformation = lazy(() => import("views/pages/user-information"));
const Verification = lazy(() => import("views/pages/verification"));
const Wallets = lazy(() => import("views/pages/wallets"));
const VirtualAccounts = lazy(() => import("views/pages/virtual-accounts"));
const WalletTransactions = lazy(
  () => import("views/containers/wallets/pages/wallet-transactions")
);
const Transactions = lazy(() => import("views/pages/transactions"));
const VirtualCards = lazy(() => import("views/pages/virtual-cards"));
const Reports = lazy(() => import("views/pages/reports"));
const TeamManagement = lazy(() => import("views/pages/team-management"));
const ExchageRates = lazy(() => import("views/pages/exchange-rate"));
const ValentineWishList = lazy(() => import("views/pages/valentine-wishlist"));
const Feature = lazy(() => import("views/pages/feature"));
const BitmamaMerchants = lazy(() => import("views/pages/bitmama-merchants"));

// Suspense wrapper component for lazy loaded routes
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingText />}>{children}</Suspense>
);

const routes: RouteObject[] = [
  {
    element: <AuthLayout />,
    children: [
      {
        element: <Login />,
        path: "/auth/login",
      },
      {
        element: <Generate2FA />,
        path: "/auth/2fa",
      },
    ],
  },
  {
    element: (
      <PrivateRoute>
        <DashboardLayout />
      </PrivateRoute>
    ),
    children: [
      {
        element: <Navigate to="/dashboard" replace />,
        path: "/",
      },
      {
        element: (
          <LazyWrapper>
            <Dashboard />
          </LazyWrapper>
        ),
        path: "/dashboard",
      },
      {
        element: (
          <LazyWrapper>
            <Request />
          </LazyWrapper>
        ),
        path: "requests",
      },
      {
        element: (
          <LazyWrapper>
            <UserInformation />
          </LazyWrapper>
        ),
        path: "user-information",
      },
      {
        element: (
          <LazyWrapper>
            <Verification />
          </LazyWrapper>
        ),
        path: "verification",
      },
      {
        element: (
          <LazyWrapper>
            <Wallets />
          </LazyWrapper>
        ),
        path: "wallets",
      },
      {
        element: (
          <LazyWrapper>
            <VirtualAccounts />
          </LazyWrapper>
        ),
        path: "virtual-accounts",
      },
      {
        element: (
          <LazyWrapper>
            <WalletTransactions />
          </LazyWrapper>
        ),
        path: "wallets/transactions",
      },
      {
        element: (
          <LazyWrapper>
            <Transactions />
          </LazyWrapper>
        ),
        path: "transactions",
      },
      {
        element: (
          <LazyWrapper>
            <VirtualCards />
          </LazyWrapper>
        ),
        path: "virtual-cards",
      },
      {
        element: (
          <LazyWrapper>
            <Reports />
          </LazyWrapper>
        ),
        path: "reports",
      },
      {
        element: (
          <LazyWrapper>
            <TeamManagement />
          </LazyWrapper>
        ),
        path: "team-management",
      },
      {
        element: (
          <LazyWrapper>
            <ExchageRates />
          </LazyWrapper>
        ),
        path: "exchange-rates",
      },
      // {
      //   element: <FX />,
      //   path: "fx"
      // },
      // {
      //   element: <WishList />,
      //   path: "wishlist"
      // },
      {
        element: (
          <LazyWrapper>
            <ValentineWishList />
          </LazyWrapper>
        ),
        path: "valentine-wishlist",
      },
      {
        element: (
          <LazyWrapper>
            <Feature />
          </LazyWrapper>
        ),
        path: "feature",
      },
      // {
      //   element: <Flights />,
      //   path: "flights"
      // },
      // {
      //   element: <Campaign />,
      //   path: "campaign"
      // },
      {
        element: (
          <LazyWrapper>
            <BitmamaMerchants />
          </LazyWrapper>
        ),
        path: "bitmama-merchants",
      },
      { element: <Unauthorized />, path: "/unauthorised" },
    ],
  },
  {
    element: <NotFound />,
    path: "*",
  },
];

export default routes;
